import {
  isToolContextState,
  ToolContextState,
} from "../../domain/ToolContext/ToolContext";
import AggregatedLogger from "../../observability/AggregatedLogger/AggregatedLogger";

export interface WebViewMessage {
  type: string;
  payload?: unknown;
}

export enum WebViewMessageType {
  PWALoaded = "PWALoaded",
  SetContext = "SetContext",
}

export interface SetContext extends WebViewMessage {
  type: WebViewMessageType.SetContext;
  payload: ToolContextState;
}

function isWebViewMessage(value: unknown): value is WebViewMessage {
  // verify if value is an object
  if (typeof value !== "object") return false;
  // verify if value is not null
  if (value === null) return false;

  const { type } = value as SetContext;

  return type !== null && type !== undefined;
}

function isValidSetContextMessage(value: unknown): value is SetContext {
  if (!isWebViewMessage(value)) return false;

  // verify its the correct message type
  if (value.type !== WebViewMessageType.SetContext) return false;

  // verify the payload is correct
  if (!isToolContextState(value.payload)) return false;

  return true;
}

export default class WebViewClient {
  protected static postMessage(data: WebViewMessage) {
    AggregatedLogger.log(
      JSON.stringify(data),
      "postMessage exists",
      window.chrome?.webview?.postMessage !== undefined,
    );
    window.chrome?.webview?.postMessage?.(JSON.stringify(data));
  }

  private static pwaLoadedRequest() {
    this.postMessage({
      type: WebViewMessageType.PWALoaded,
    });
    AggregatedLogger.log("sent PWALoaded");
  }

  public static registerSetContextListener(
    setContext: (context: ToolContextState) => void,
  ) {
    const handleMessageEvent = (event: MessageEvent<unknown>) => {
      if (isValidSetContextMessage(event.data)) {
        AggregatedLogger.log(
          `received valid ${WebViewMessageType.SetContext}`,
          {
            ...event.data,
            token: "***sanitized***", // Scrub out the token
          },
        );
        setContext(event.data.payload);
      } else if (isWebViewMessage(event.data)) {
        AggregatedLogger.log(
          `received ${event.data.type}, but looking for ${WebViewMessageType.SetContext}`,
          event.data,
        );
      } else {
        AggregatedLogger.log("received unknown message", event.data);
      }
    };

    window.addEventListener("message", handleMessageEvent);
    WebViewClient.pwaLoadedRequest();

    return () => {
      window.removeEventListener("message", handleMessageEvent);
    };
  }
}
